import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import type { Application } from '@/types';

export type CompanyApplicationsPage = {
  items: Application[];
  hasMore: boolean;
  nextCursor?: string;
};

export type CompanyApplicationsOrderBy = 'appliedAt' | 'updatedAt';

/** API page size (UI table stays at 20 rows; each fetch covers ~2–3 table pages). */
export const COMPANY_APPLICATIONS_FETCH_SIZE = 50;

/** Warm-up fetches after first load so early table pages do not wait on the network. */
export const COMPANY_APPLICATIONS_INITIAL_PREFETCH_PAGES = 2;

/** Server-side list scope for company applications infinite query. */
export type CompanyApplicationsListScope = {
  archived: boolean;
  state?: string;
  jobPositionId?: string;
  orderBy: CompanyApplicationsOrderBy;
};

export function buildCompanyApplicationsListScope(input: {
  archived: boolean;
  statusFilter: string;
  positionFilter: string;
  sortColumn: string;
  sortDirection: 'asc' | 'desc';
}): CompanyApplicationsListScope {
  return {
    archived: input.archived,
    state: serverStateFromStatusFilter(input.statusFilter),
    jobPositionId: input.positionFilter !== 'all' ? input.positionFilter : undefined,
    orderBy: resolveServerListOrderBy(input.sortColumn),
  };
}

/** True when list API should pass filters (status, position). PIPELINE_STUCK stays client-only. */
export function scopeUsesServerFilters(scope: CompanyApplicationsListScope): boolean {
  return Boolean(scope.state || scope.jobPositionId);
}

export function applicationMatchesServerScope(
  app: Pick<Application, 'state' | 'positionId'>,
  scope: CompanyApplicationsListScope,
): boolean {
  if (scope.state && app.state !== scope.state) return false;
  if (scope.jobPositionId && app.positionId !== scope.jobPositionId) return false;
  return true;
}

/**
 * Union of all `applications-list` pages for this company + tab, filtered to match scope.
 * Used for instant UI while a scoped query loads; does not drive pagination cursors.
 */
export function collectApplicationsFromListCaches(
  queryClient: QueryClient,
  companyId: string,
  scope: CompanyApplicationsListScope,
): Application[] {
  const entries = queryClient.getQueriesData<InfiniteData<CompanyApplicationsPage>>({
    queryKey: ['applications-list', companyId],
  });

  const byId = new Map<string, Application>();
  for (const [key, data] of entries) {
    if (!data?.pages?.length) continue;
    const keyScope = (key as readonly unknown[])[2] as CompanyApplicationsListScope | undefined;
    if (!keyScope || keyScope.archived !== scope.archived) continue;
    if (keyScope.orderBy !== scope.orderBy) continue;
    for (const app of flattenApplicationPages(data.pages)) {
      if (!applicationMatchesServerScope(app, scope)) continue;
      const prev = byId.get(app.id);
      byId.set(app.id, prev ? preferNewerApplicationRow(prev, app) : app);
    }
  }

  return Array.from(byId.values());
}

/** Maps UI status filter to BE `state` query (PIPELINE_STUCK is client-only). */
export function serverStateFromStatusFilter(statusFilter: string): string | undefined {
  if (statusFilter === 'all' || statusFilter === 'PIPELINE_STUCK') return undefined;
  return statusFilter;
}

/**
 * Maps the active table sort column to the list API `orderBy` field.
 * Default is `updatedAt` (most recently changed first — processing, new applications).
 * Use `appliedAt` when the user sorts by the Applied column.
 */
export function resolveServerListOrderBy(sortColumn: string): CompanyApplicationsOrderBy {
  return sortColumn === 'appliedAt' ? 'appliedAt' : 'updatedAt';
}

export function timestampMs(
  value: Application['updatedAt'] | Application['appliedAt'] | undefined,
): number | null {
  if (!value) return null;
  if (typeof value === 'string') {
    const ms = Date.parse(value);
    return Number.isNaN(ms) ? null : ms;
  }
  if (typeof value === 'object' && '_seconds' in value) {
    return value._seconds * 1000 + Math.floor((value._nanoseconds ?? 0) / 1e6);
  }
  return null;
}

/** Recency for dedup — matches Updated sort fallback (`updatedAt` → `appliedAt`). */
export function applicationRowFreshnessMs(
  app: Pick<Application, 'updatedAt' | 'appliedAt'>,
): number {
  return timestampMs(app.updatedAt) ?? timestampMs(app.appliedAt) ?? 0;
}

/** When the same id appears in multiple list pages/caches, keep the fresher row. */
export function preferNewerApplicationRow(
  existing: Application,
  incoming: Application,
): Application {
  return applicationRowFreshnessMs(incoming) >= applicationRowFreshnessMs(existing)
    ? incoming
    : existing;
}

/** Union list slices deduped by id; newer `updatedAt` wins (cursor pagination + cache patch). */
export function mergeApplicationListsPreferNewest(
  ...lists: Application[][]
): Application[] {
  const byId = new Map<string, Application>();
  for (const list of lists) {
    for (const app of list) {
      const prev = byId.get(app.id);
      byId.set(app.id, prev ? preferNewerApplicationRow(prev, app) : app);
    }
  }
  return Array.from(byId.values());
}

/** Client-side sort key; `updatedAt` falls back to `appliedAt` for legacy rows. */
export function getApplicationListSortValue(
  app: Pick<Application, 'updatedAt' | 'appliedAt'> & Record<string, unknown>,
  column: string,
): unknown {
  if (column === 'updatedAt') {
    return timestampMs(app.updatedAt) ?? timestampMs(app.appliedAt) ?? 0;
  }
  if (column === 'appliedAt') {
    return timestampMs(app.appliedAt) ?? 0;
  }
  return app[column];
}

export function flattenApplicationPages(
  pages: { items: Application[] }[] | undefined,
): Application[] {
  if (!pages?.length) return [];
  const byId = new Map<string, Application>();
  for (const page of pages) {
    for (const app of page.items) {
      const prev = byId.get(app.id);
      byId.set(app.id, prev ? preferNewerApplicationRow(prev, app) : app);
    }
  }
  return Array.from(byId.values());
}

